/**
 * AdminAlerts — unified view of admin alerts:
 *   - device_action (auto-generated from device inspections)
 *   - mispriced_service (existing virtual alerts from technician flags)
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Bell, CheckCircle2, Cog, DollarSign, Filter, MapPin, RotateCcw, Truck } from "lucide-react";

const SEVERITY_STYLES = {
    high:   { color: "bg-red-100 text-red-800 border-red-200",       label: "Alta",  icon: "text-red-600" },
    medium: { color: "bg-orange-100 text-orange-800 border-orange-200", label: "Media", icon: "text-orange-600" },
    low:    { color: "bg-amber-50 text-amber-700 border-amber-200",  label: "Baja",  icon: "text-amber-600" },
};

const CATEGORY_LABELS = {
    device_action: "Dispositivo",
    mispriced_service: "Precio",
    vehicle_added: "Vehículo",
    device_request: "Solicitud",
};

const DEVICE_TYPE_LBL = {
    cebadero_cebo: "Cebadero",
    trampa: "Trampa",
    lampara_uv: "Lámpara UV",
};
const ZONE_LBL = { verde: "Verde (perímetro exterior)", amarilla: "Amarilla (puntos focales interiores)", roja: "Roja (áreas de acceso al cliente)" };

function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("es-SV", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso?.slice(0, 16) || ""; }
}

export default function AdminAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusTab, setStatusTab] = useState("open");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [resolveTarget, setResolveTarget] = useState(null);
    const [resolveNote, setResolveNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("status", statusTab);
            if (categoryFilter !== "all") params.set("category", categoryFilter);
            const r = await api.get(`/admin/alerts?${params.toString()}`);
            setAlerts(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudieron cargar las alertas");
            setAlerts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [statusTab, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const resolve = async () => {
        if (!resolveTarget) return;
        setSubmitting(true);
        try {
            await api.patch(`/admin/alerts/${resolveTarget.id}/resolve`, { resolution_note: resolveNote });
            toast.success("Alerta marcada como resuelta");
            setResolveTarget(null);
            setResolveNote("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo resolver");
        } finally {
            setSubmitting(false);
        }
    };

    const reopen = async (alert) => {
        try {
            await api.patch(`/admin/alerts/${alert.id}/reopen`);
            toast.success("Alerta reabierta");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo reabrir");
        }
    };

    const counts = {
        open: alerts.filter((a) => a.status === "open").length,
    };

    return (
        <div className="space-y-4" data-testid="admin-alerts-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        <Bell className="w-6 h-6 text-[#FF0000]" />
                        Alertas del administrador
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Hallazgos de dispositivos, solicitudes de técnicos y alertas de precio.
                    </p>
                </div>
            </div>

            {/* Filters */}
            <Card className="p-3 flex flex-wrap items-center gap-3">
                <Tabs value={statusTab} onValueChange={setStatusTab}>
                    <TabsList data-testid="alerts-status-tabs">
                        <TabsTrigger value="open" data-testid="alerts-tab-open">Abiertas</TabsTrigger>
                        <TabsTrigger value="resolved" data-testid="alerts-tab-resolved">Resueltas</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="flex items-center gap-1.5 text-sm">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <button
                        onClick={() => setCategoryFilter("all")}
                        className={`px-2 py-1 rounded text-xs ${categoryFilter === "all" ? "bg-slate-200 font-semibold" : "hover:bg-slate-100"}`}
                        data-testid="alerts-filter-all"
                    >Todas</button>
                    <button
                        onClick={() => setCategoryFilter("device_action")}
                        className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${categoryFilter === "device_action" ? "bg-slate-200 font-semibold" : "hover:bg-slate-100"}`}
                        data-testid="alerts-filter-device"
                    >
                        <Cog className="w-3 h-3" /> Dispositivos
                    </button>
                    <button
                        onClick={() => setCategoryFilter("mispriced_service")}
                        className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${categoryFilter === "mispriced_service" ? "bg-slate-200 font-semibold" : "hover:bg-slate-100"}`}
                        data-testid="alerts-filter-mispriced"
                    >
                        <DollarSign className="w-3 h-3" /> Precio
                    </button>
                    <button
                        onClick={() => setCategoryFilter("vehicle_added")}
                        className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${categoryFilter === "vehicle_added" ? "bg-slate-200 font-semibold" : "hover:bg-slate-100"}`}
                        data-testid="alerts-filter-vehicle"
                    >
                        <Truck className="w-3 h-3" /> Vehículos
                    </button>
                    <button
                        onClick={() => setCategoryFilter("device_request")}
                        className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${categoryFilter === "device_request" ? "bg-slate-200 font-semibold" : "hover:bg-slate-100"}`}
                        data-testid="alerts-filter-device-request"
                    >
                        <MapPin className="w-3 h-3" /> Solicitudes
                    </button>
                </div>
            </Card>

            {/* List */}
            {loading ? (
                <Card className="p-6 text-center text-muted-foreground">Cargando alertas...</Card>
            ) : alerts.length === 0 ? (
                <Card className="p-10 text-center border-dashed">
                    <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
                    <div className="font-medium">Sin alertas {statusTab === "open" ? "abiertas" : "resueltas"}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                        {statusTab === "open"
                            ? "¡Buen trabajo! No hay nada pendiente por revisar."
                            : "Aún no se ha resuelto ninguna alerta."}
                    </p>
                </Card>
            ) : (
                <ul className="space-y-2" data-testid="alerts-list">
                    {alerts.map((a) => {
                        const sev = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.medium;
                        const isMispriced = a.category === "mispriced_service";
                        return (
                            <li key={a.id} data-testid={`alert-row-${a.id}`}>
                                <Card className={`p-3 border-l-4 ${a.severity === "high" ? "border-l-red-600" : a.severity === "medium" ? "border-l-orange-500" : "border-l-amber-400"}`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${sev.color}`}>
                                            {isMispriced ? <DollarSign className={`w-4 h-4 ${sev.icon}`} /> : a.category === "device_request" ? <MapPin className={`w-4 h-4 ${sev.icon}`} /> : <AlertTriangle className={`w-4 h-4 ${sev.icon}`} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-semibold text-sm">{a.title}</span>
                                                <Badge variant="outline" className={`text-[10px] ${sev.color}`}>
                                                    {sev.label}
                                                </Badge>
                                                <Badge variant="outline" className="text-[10px]">
                                                    {CATEGORY_LABELS[a.category] || a.category}
                                                </Badge>
                                                {a.status === "resolved" && (
                                                    <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                                                        <CheckCircle2 className="w-3 h-3 mr-0.5" /> Resuelta
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-600 mt-1">
                                                {a.client_name && <span className="font-medium">{a.client_name}</span>}
                                                {a.device_number && (
                                                    <>
                                                        <span className="text-slate-400 mx-1">·</span>
                                                        <span>
                                                            {a.device_number}
                                                            {a.device_type && ` (${DEVICE_TYPE_LBL[a.device_type] || a.device_type})`}
                                                            {a.zone && ` · zona ${ZONE_LBL[a.zone] || a.zone}`}
                                                            {a.area_name && ` · ${a.area_name}`}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-700 mt-1">{a.message}</p>
                                            <div className="text-[10px] text-muted-foreground mt-1.5 flex flex-wrap gap-x-2">
                                                <span>📅 {fmtDate(a.created_at)}</span>
                                                {a.resolved_at && <span>✓ resuelta: {fmtDate(a.resolved_at)}</span>}
                                                {a.resolution_note && <span className="italic">&quot;{a.resolution_note}&quot;</span>}
                                            </div>
                                        </div>
                                        <div className="shrink-0">
                                            {a.status === "open" ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => { setResolveTarget(a); setResolveNote(""); }}
                                                    data-testid={`alert-resolve-${a.id}`}
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolver
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => reopen(a)}
                                                    data-testid={`alert-reopen-${a.id}`}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reabrir
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {a.category === "mispriced_service" && a.source_report_id && (
                                        <div className="mt-2 pl-12">
                                            <Link
                                                to={`/admin/reports?mispriced=1`}
                                                className="text-[11px] text-blue-700 hover:underline"
                                            >
                                                Ver reporte de servicio →
                                            </Link>
                                        </div>
                                    )}
                                </Card>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* Resolve dialog */}
            <Dialog open={!!resolveTarget} onOpenChange={(v) => !v && setResolveTarget(null)}>
                <DialogContent data-testid="resolve-dialog">
                    <DialogHeader>
                        <DialogTitle>Resolver alerta</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm">
                            <div className="font-semibold">{resolveTarget?.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{resolveTarget?.message}</div>
                        </div>
                        <div>
                            <label className="text-xs font-medium">Nota de resolución (opcional)</label>
                            <Textarea
                                rows={3}
                                value={resolveNote}
                                onChange={(e) => setResolveNote(e.target.value)}
                                placeholder="Ej: Se envió cebadero nuevo en próxima ruta..."
                                data-testid="resolve-note"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResolveTarget(null)}>Cancelar</Button>
                        <Button onClick={resolve} disabled={submitting} data-testid="resolve-confirm">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como resuelta
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
